import { useEffect, useState } from "react";
import { AlertTriangle, Eye, EyeOff } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { makeRequest } from "@/utils/makeRequest";
import { getErrorMessage } from "@/utils/getErrorMessage";

import { toast } from "sonner";

import { useAppSelector } from "@/store/hooks";

import { Spinner } from "../ui/spinner";
import { OtpInput } from "../shared/OtpInput";

const OTP_LENGTH = 6;
const OTP_COOLDOWN_SECONDS = 60;

interface UserVerificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVerify: (data: { otp: string; securityCode: string }) => Promise<void>;
  loading: boolean;
  isP2PRelease?: boolean;
  isSwap?: boolean;
}

export function UserVerificationDialog({
  open,
  onOpenChange,
  onVerify,
  isP2PRelease,
  isSwap,
}: UserVerificationDialogProps) {
  const [securityCode, setSecurityCode] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const [showSecurityCode, setShowSecurityCode] = useState(false);

  const auth = useAppSelector((state) => state.auth);

  const user = auth?.user;

  useEffect(() => {
    if (!open) {
      const timeout = setTimeout(() => {
        setSecurityCode("");
        setShowSecurityCode(false);
      }, 200);

      return () => clearTimeout(timeout);
    }
  }, [open]);

  const handleRequestOtp = async () => {
    if (!user?.email) {
      throw new Error("User email address is unavailable.");
    }

    const res = await makeRequest.put("/auth/email/verify/otp/two-fa", {
      email: user.email,
    });

    const data = res?.data;

    console.log("USER VERIFICATION OTP DATA", data);

    return {
      resendCooldownSeconds:
        data?.otp?.resendCooldownSeconds ?? OTP_COOLDOWN_SECONDS,

      message: data?.message || "OTP sent successfully.",
    };
  };

  const handleVerify = async (otp: string) => {
    if (!securityCode) {
      toast.error("Please enter your security code.");

      return;
    }

    if (!isSwap && !otp) {
      toast.error("Please enter the verification code.");

      return;
    }

    setIsLoading(true);

    try {
      await onVerify({
        otp,
        securityCode,
      });

      setSecurityCode("");
      setShowSecurityCode(false);

      onOpenChange(false);
    } catch (error) {
      /**
       * We intentionally rethrow here.
       *
       * OtpInput handles OTP errors itself.
       * This allows the parent onVerify error to
       * be displayed here without swallowing it.
       */
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="flex items-center justify-center flex-col mb-5">
          <AlertTriangle className="w-10 h-10" />

          <DialogTitle className="text-yellow-500 text-center">
            Transaction Verification
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {isP2PRelease && (
            <div className="rounded-xl border bg-muted/30 p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5 shrink-0" />

                <div>
                  <p className="text-sm font-medium">Security Reminder</p>

                  <p className="mt-1 text-xs leading-5 text-muted-foreground">
                    By verifying this transaction, you confirm that payment has
                    been received and independently verified. Released assets
                    cannot be recovered once transferred to the buyer.
                  </p>

                  <div className="mt-3 rounded-lg border bg-background px-3 py-2">
                    <p className="text-xs font-medium">
                      Never release assets based solely on:
                    </p>

                    <p className="mt-1 text-xs text-muted-foreground">
                      Screenshots • SMS alerts • Email notifications • Chat
                      confirmations
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isSwap && (
            <div className="space-y-2">
              <Label className="text-center w-full flex items-center justify-center mb-2">
                One-Time Password (OTP)
              </Label>

              <OtpInput
                length={OTP_LENGTH}
                cooldown={OTP_COOLDOWN_SECONDS}
                onRequestOtp={handleRequestOtp}
                onVerifyOtp={handleVerify}
                requestLabel="Request OTP"
                resendLabel="Resend OTP"
                verifyLabel="Verify"
                verifyDisabled={!securityCode || isLoading}
              />
            </div>
          )}

          {/* =================================================
              SECURITY CODE
          ================================================= */}

          <div className="space-y-2">
            <Label>Security Code</Label>

            <div className="relative">
              <Input
                disabled={isLoading || (!isSwap && false)}
                className="h-11 pr-10"
                type={showSecurityCode ? "text" : "password"}
                value={securityCode}
                onChange={(e) => setSecurityCode(e.target.value)}
                placeholder="Enter security code"
              />

              <button
                type="button"
                onClick={() => setShowSecurityCode((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                aria-label={
                  showSecurityCode ? "Hide security code" : "Show security code"
                }
              >
                {showSecurityCode ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {/* =================================================
              SWAP ACTIONS
              
              For swap there is no OTP, therefore the
              dialog still needs its own Verify button.
          ================================================= */}

          {isSwap && (
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>

              <Button
                className="flex-1"
                onClick={async () => {
                  if (!securityCode) {
                    return;
                  }

                  setIsLoading(true);

                  try {
                    await onVerify({
                      otp: "",
                      securityCode,
                    });

                    setSecurityCode("");
                    setShowSecurityCode(false);

                    onOpenChange(false);
                  } catch (error) {
                    toast.error(getErrorMessage(error));
                  } finally {
                    setIsLoading(false);
                  }
                }}
                disabled={isLoading || !securityCode}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Spinner />
                    Processing...
                  </div>
                ) : (
                  "Verify"
                )}
              </Button>
            </div>
          )}

          {/* =================================================
              CANCEL FOR NON-SWAP
          ================================================= */}

          {!isSwap && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
